import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Loader2 } from "lucide-react";
import { ownerWaitlist } from "../content/site";
import { submitOwnerRegistration } from "../lib/submitHandlers";
import { Button, Container, Field, PillSelect, Reveal, TextInput } from "./ui";

const EMPTY = { name: "", mobile: "", location: "", spaceType: "", numberOfSpaces: "" };

function validate(form) {
  const errors = {};
  if (!form.name.trim()) errors.name = "Please enter your name.";
  if (!/^[6-9]\d{9}$/.test(form.mobile.trim()))
    errors.mobile = "Enter a valid 10-digit mobile number.";
  if (!form.location.trim()) errors.location = "Please enter a location.";
  if (!form.spaceType) errors.spaceType = "Please select a space type.";
  if (!form.numberOfSpaces || Number(form.numberOfSpaces) < 1)
    errors.numberOfSpaces = "Enter the number of spaces (at least 1).";
  return errors;
}

export default function OwnerRegistration() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const setField = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = validate(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setStatus("submitting");
    const result = await submitOwnerRegistration(form);
    if (result.ok) {
      setStatus("success");
      setForm(EMPTY);
    } else {
      setStatus("idle");
    }
  };

  return (
    <section id="owner-waitlist" className="bg-[var(--color-bg-light-orange)] py-20 sm:py-28">
      <Container>
        <Reveal className="mx-auto max-w-xl">
          <div className="rounded-[32px] border border-primary/15 bg-white p-6 shadow-navy sm:p-10">
            <AnimatePresence mode="wait">
              {status === "success" ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex flex-col items-center py-10 text-center"
                >
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-success/10 text-success">
                    <CheckCircle2 size={34} />
                  </span>
                  <h3 className="mt-6 text-2xl font-extrabold text-navy">
                    {ownerWaitlist.successTitle}
                  </h3>
                  <p className="mt-2 text-base font-semibold text-navy/50">
                    {ownerWaitlist.successSub}
                  </p>
                </motion.div>
              ) : (
                <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <h2 className="text-center text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">
                    {ownerWaitlist.headline}
                  </h2>
                  <p className="mt-2 text-center text-sm font-semibold text-navy/50">
                    {ownerWaitlist.cta}
                  </p>

                  <form onSubmit={handleSubmit} noValidate className="mt-8 flex flex-col gap-5">
                    <div className="grid gap-5 sm:grid-cols-2">
                      <Field label="Name" id="or-name" error={errors.name}>
                        <TextInput
                          id="or-name"
                          name="name"
                          autoComplete="name"
                          placeholder="Your full name"
                          value={form.name}
                          onChange={setField("name")}
                          error={errors.name}
                        />
                      </Field>
                      <Field label="Mobile" id="or-mobile" error={errors.mobile}>
                        <TextInput
                          id="or-mobile"
                          name="mobile"
                          inputMode="numeric"
                          autoComplete="tel"
                          placeholder="98765 43210"
                          value={form.mobile}
                          onChange={setField("mobile")}
                          error={errors.mobile}
                        />
                      </Field>
                    </div>

                    <Field label="Location" id="or-location" error={errors.location}>
                      <TextInput
                        id="or-location"
                        name="location"
                        placeholder="e.g. RS Puram, Coimbatore"
                        value={form.location}
                        onChange={setField("location")}
                        error={errors.location}
                      />
                    </Field>

                    <Field label="Type of Space" error={errors.spaceType}>
                      <PillSelect
                        name="spaceType"
                        options={ownerWaitlist.spaceTypes}
                        value={form.spaceType}
                        onChange={setField("spaceType")}
                      />
                    </Field>

                    <Field label="Number of Spaces" id="or-count" error={errors.numberOfSpaces}>
                      <TextInput
                        id="or-count"
                        name="numberOfSpaces"
                        type="number"
                        min="1"
                        placeholder="e.g. 2"
                        value={form.numberOfSpaces}
                        onChange={setField("numberOfSpaces")}
                        error={errors.numberOfSpaces}
                      />
                    </Field>

                    <Button
                      type="submit"
                      size="lg"
                      className="mt-2 w-full"
                      disabled={status === "submitting"}
                    >
                      {status === "submitting" ? (
                        <>
                          <Loader2 size={18} className="animate-spin" /> Submitting…
                        </>
                      ) : (
                        ownerWaitlist.submitCta
                      )}
                    </Button>
                  </form>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </Container>
    </section>
  );
}
